import DesignTokens from '@tableau/design-tokens';
import {
  ButtonType,
  ButtonWidget,
  CloseButtonWidget,
  CompactInlineAlertWidget,
  InlineAlertType,
  TextLinkKind,
  TextLinkWidget,
} from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import {
  GenerativeAiCheckRegistrationState,
  useGenerativeAICheckRegistrationHook,
} from '~src/settings/general-settings/components/sections/tableau-gai/GenerativeAICheckRegistrationApi';
import {
  GenerativeAIRegistrationHookType,
  GenerativeAiRegistrationState,
} from '~src/settings/general-settings/components/sections/tableau-gai/GenerativeAIRegistrationApi';
import { applyColor } from '~src/style/ColorService';

import { Messages } from '../../../../../codegen/Localize';
import { learnMoreStyle } from '../../../../styles/CommonStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import {
  GAI_EMBEDDINGS_MODEL_SITE_SETTING,
  GAI_IS_DEFAULT_ONLINE_MODELS,
  GAI_QUERY_PARSING_MODEL_SITE_SETTING,
  GAI_VIZ_AND_CALC_GENERATION_MODEL_SITE_SETTING,
} from './GenerativeAITypes';
import { ModelSettingsSection } from './ModelSettingsSection';
import { SelectModelsOnlineDialog,SelectModelsOnlineDialogProps, SelectModelsOnlineDialogState } from './SelectModelsOnlineDialog';

export type GenerativeAIRegistrationProps = {
  name?: string;
  testId?: string;
  gaiHook: GenerativeAIRegistrationHookType;
  connectionDetails?: string;
};

function getFriendlyDomainName(domain: string): string {
  try {
    return new URL(domain).host;
  } catch {
    return domain;
  }
}

function RegistrationAlert(props: { registrationStateCode: string | null, closeAlert: () => void }) {
  if (props.registrationStateCode === null || props.registrationStateCode === undefined) {
    return null;
  }

  const code = Number(props.registrationStateCode);
  let message = Messages.tableauGAISiteSettingsTestNotRegistered();
  let messageType = InlineAlertType.Error;

  if (code === 0) {
    message = Messages.tableauGAISiteSettingsTestSuccess();
    messageType = InlineAlertType.Success;
  } else if (code === 1) {
    message = Messages.tableauGAISiteSettingsTestFailed();
    messageType = InlineAlertType.Warning;
  }

  return (
    <p style={{ paddingBottom: 0, marginBottom: 0 }}>
      <CompactInlineAlertWidget testId={'tableau-ai-success'} style={{ padding: CommonStyles.Sizes.MediumSpace }} type={messageType}>
        <div style={{ display: 'flex' }}>
          <div style={{ width: '90%', float: 'left' }}>{message}</div>
          <div style={{ width: '10%', float: 'right', textAlign: 'right' }}>
            <CloseButtonWidget onClick={props.closeAlert} />
          </div>
        </div>
      </CompactInlineAlertWidget>
    </p>
  );
}

function RegistrationDetails(props: {
  tableauGAIConnectHelpUrl: string;
  registrationState: GenerativeAiRegistrationState;
  registerEnterprise: () => void;
  checkRegistration: () => void;
  resetRegistrationCheck: () => void;
  unregisterEnterprise: () => void;
  busy: boolean;
  checkRegistrationState: GenerativeAiCheckRegistrationState;
}) {
  return (
    <>
      <div id="tableau-gai-customer-id">
        <p style={{ display: 'inline' }}>{Messages.tableauGAISiteSettingsConnected()}</p>
        <TextLinkWidget
          className={learnMoreStyle}
          href={props.tableauGAIConnectHelpUrl}
          target="_blank"
          testId={'tableau-gai-connect-learn-more'}
        >
          {Messages.learnMore()}
        </TextLinkWidget>
      </div>
      <p style={{ display: 'table' }}>
        <table
          style={{
            backgroundColor: applyColor(DesignTokens.color.elevation.border.default.subtle, '#f7f5f6'),
            paddingBottom: CommonStyles.Sizes.MediumSpace,
            paddingTop: CommonStyles.Sizes.MediumSpace,
          }}
        >
          <tbody>
            <tr>
              <td style={{ width: '100%', paddingLeft: CommonStyles.Sizes.MediumSpace }}>
                <div style={{ float: 'right', whiteSpace: 'nowrap' }}>{Messages.tableauGAISiteSettingsSalesforceOrg()}</div>
                <div style={{ float: 'right', whiteSpace: 'nowrap' }}>{Messages.tableauGAISiteSettingsUsername()}</div>
              </td>
              <td style={{ width: '100%', alignContent: 'left' }}>
                <div>
                  <TextLinkWidget
                    href={props.registrationState.domain}
                    kind={TextLinkKind.Standard}
                    target="_blank"
                    testId="link-to-salesforce-instance"
                  >
                    {getFriendlyDomainName(props.registrationState.domain ?? '')}
                  </TextLinkWidget>
                </div>
                <div data-tb-test-id={'username-salesforce-connect'}>{props.registrationState.username}</div>
              </td>
              <td
                style={{ whiteSpace: 'nowrap', paddingRight: CommonStyles.Sizes.MediumSpace, paddingLeft: CommonStyles.Sizes.MediumSpace }}
              >
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  testId="update-to-salesforce-button"
                  className="tb-outline-button tb-margin-left-narrow"
                  onClick={() => props.registerEnterprise()}
                >
                  {Messages.tableauGAISiteSettingsActionEditButton()}
                </ButtonWidget>
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  style={{ marginLeft: CommonStyles.Sizes.MediumSpace }}
                  testId="test-to-salesforce-button"
                  className="tb-outline-button tb-margin-left-narrow"
                  onClick={() => props.checkRegistration()}
                >
                  {props.busy ? <span className="tb-icon-spinner tb-lr-padded-narrow" /> : null}
                  <span>{Messages.tableauGAISiteSettingsActionTestButton()}</span>
                </ButtonWidget>
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  style={{ marginLeft: CommonStyles.Sizes.MediumSpace }}
                  testId="delete-to-salesforce-button"
                  className="tb-outline-button tb-margin-left-narrow"
                  onClick={() => props.unregisterEnterprise()}
                >
                  {Messages.tableauGAISiteSettingsActionDeleteButton()}
                </ButtonWidget>
              </td>
            </tr>
          </tbody>
        </table>
        <RegistrationAlert registrationStateCode={props.checkRegistrationState.code} closeAlert={() => props.resetRegistrationCheck()} />
      </p>
    </>
  );
}

function ConnectToSalesforceButton(props: {
  tableauGAIConnectHelpUrl: string;
  registerEnterprise: () => void;
  connectionDetails: string;
  alertMessage: { type: InlineAlertType | null, text: React.ReactNode, dismissible?: boolean } | null;
}) {
  return (
    <>
      <div id="tableau-gai-customer-id">
        <p style={{ display: 'inline' }}>{props.connectionDetails ?? Messages.tableauGAISiteSettingsConnect()}</p>
        <TextLinkWidget
          className={learnMoreStyle}
          href={props.tableauGAIConnectHelpUrl}
          target="_blank"
          testId={'tableau-gai-connect-learn-more'}
        >
          {Messages.learnMore()}
        </TextLinkWidget>
      </div>
      <div style={{ marginTop: CommonStyles.Sizes.MediumSpace }}>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          testId="connect-to-salesforce-button"
          className="tb-outline-button tb-margin-left-narrow"
          onClick={() => props.registerEnterprise()}
        >
          {Messages.tableauGAISiteSettingsActionConnectButton()}
        </ButtonWidget>
        {props.alertMessage && props.alertMessage.type && (
          <div style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}>
            <CompactInlineAlertWidget
              testId={'tableau-ai-alert'}
              style={{ padding: CommonStyles.Sizes.MediumSpace }}
              type={props.alertMessage.type}
            >
              {props.alertMessage.text}
            </CompactInlineAlertWidget>
          </div>
        )}
      </div>
    </>
  );
}

export function GenerativeAIRegistration(props: GenerativeAIRegistrationProps) {
  const { registrationState, unregisterEnterprise, registerEnterprise, alertMessage, resetAlertMessage } = props.gaiHook;
  const { checkRegistrationState, busy, checkRegistration, resetRegistrationCheck } = useGenerativeAICheckRegistrationHook();
  const [hasEditModelsError, setHasEditModelsError] = React.useState(false);
  const [selectModelsDialogProps, setSelectModelsDialogProps] = React.useState<SelectModelsOnlineDialogProps | null>(null);
  const formik = useFormikContext<any>();

  React.useEffect(() => {
    if (registrationState.registered && alertMessage) {
      resetAlertMessage();
    }
  }, [registrationState.registered, alertMessage, resetAlertMessage]);

  async function requestSelectModelsDialog(): Promise<void> {
    setHasEditModelsError(false);

    const response = await checkRegistration(true);
    if (response.generativeAiCheckRegistration.code !== '0') {
      setHasEditModelsError(true);
      return;
    }

    const dialogProps: SelectModelsOnlineDialogProps = {
      onClose: () => setSelectModelsDialogProps(null),
      onSubmit: (formikValues: SelectModelsOnlineDialogState) => {
        formik.setFieldValue(GAI_QUERY_PARSING_MODEL_SITE_SETTING, formikValues.modelUnderstandUserRequest);
        formik.setFieldValue(GAI_VIZ_AND_CALC_GENERATION_MODEL_SITE_SETTING, formikValues.modelVizAndCalcGeneration);
        formik.setFieldValue(GAI_EMBEDDINGS_MODEL_SITE_SETTING, formikValues.modelProcessDataContext);
        formik.setFieldValue(GAI_IS_DEFAULT_ONLINE_MODELS, formikValues.modelIsDefaultOnlineModels);
      },
      initialValues: {
        modelUnderstandUserRequest: formik.values.settings.siteSettingsV2.generativeAiQueryParsingModel || '',
        modelVizAndCalcGeneration: formik.values.settings.siteSettingsV2.generativeAiVizAndCalcGenerationModel || '',
        modelProcessDataContext: formik.values.settings.siteSettingsV2.generativeAiEmbeddingsModel || '',
        modelIsDefaultOnlineModels: formik.values.settings.siteSettingsV2.generativeAiIsDefaultOnlineModels || false,
      },
    };
    setSelectModelsDialogProps(dialogProps);
  }

  const tableauGAIConnectHelpUrl = HelpService.getHelpUrlByCategory('gpt-salesforce-login');

  if (!FeatureFlags.tabGAITokenServiceEnterpriseRegistration) {
    return null;
  }

  return (
    <>
      {registrationState.registered ? (
        <RegistrationDetails
          tableauGAIConnectHelpUrl={tableauGAIConnectHelpUrl}
          registrationState={registrationState}
          registerEnterprise={registerEnterprise}
          checkRegistration={checkRegistration}
          resetRegistrationCheck={resetRegistrationCheck}
          unregisterEnterprise={unregisterEnterprise}
          busy={busy}
          checkRegistrationState={checkRegistrationState}
        />
      ) : (
        <ConnectToSalesforceButton
          tableauGAIConnectHelpUrl={tableauGAIConnectHelpUrl}
          registerEnterprise={registerEnterprise}
          connectionDetails={props.connectionDetails}
          alertMessage={alertMessage}
        />
      )}
      {FeatureFlags.bringYourOwnLLMForCloudEnabled && (
        <>
          <ModelSettingsSection
            isOnlineEnvironment={true}
            isConnectionValid={registrationState.registered && !alertMessage}
            isOpenAIProvider={true}
            openSelectModelsDialog={requestSelectModelsDialog}
            queryParsingModel={formik.values.settings?.siteSettingsV2?.generativeAiQueryParsingModel || ''}
            vizAndCalcGenerationModel={formik.values.settings?.siteSettingsV2?.generativeAiVizAndCalcGenerationModel || ''}
            embeddingsModel={formik.values.settings?.siteSettingsV2?.generativeAiEmbeddingsModel || ''}
            isDefaultOnlineModels={formik.values.settings?.siteSettingsV2?.generativeAiIsDefaultOnlineModels || false}
            showEditModelsError={hasEditModelsError}
            dismissEditModelsError={() => setHasEditModelsError(false)}
          />
          {selectModelsDialogProps && <SelectModelsOnlineDialog {...selectModelsDialogProps} />}
        </>
      )}
    </>
  );
}

export const GenerativeAIRegistrationSectionSpecs = componentRegistryEntry({
  name: 'GenerativeAIRegistrationSection',
  component: GenerativeAIRegistration,
});
